import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, finalize } from 'rxjs/operators';
import { Position, RepoBusiness, RepoContact } from 'src/app/models/repo-business/repo-business.model';
import { environment } from '../../../environments/environment';
import { SpinnerService } from './spinner.service';

@Injectable({
  providedIn: 'root',
})
export class RepoBusinessService {
  
  
  private readonly baseUrl = environment.api2BaseUrl;
  public onError: BehaviorSubject<string> = new BehaviorSubject('');
  public serviceParameter = {
    'Content-Type': 'application/json; charset=utf-8',
    Accept: 'application/json',
    Authorization: '',
  };

  constructor(private readonly http: HttpClient, private readonly spinnerService: SpinnerService) { } // Inject SpinnerService

  getRepoBusinesses(): Observable<RepoBusiness[]> {
    this.spinnerService.addRequest('getRepoBusinesses'); // Show spinner
    return this.http
      .get<RepoBusiness[]>(`${this.baseUrl}/api/RepoBusiness/RepoBusinesses`)
      .pipe(
        map((data: RepoBusiness[]) => data),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getRepoBusinesses')) // Hide spinner on completion
      );
  }

  getPositions(): Observable<Position[]> {
    this.spinnerService.addRequest('getPositions'); // Show spinner
    return this.http
      .get<Position[]>(`${this.baseUrl}/api/RepoBusiness/Positions`)
      .pipe(
        map((data: Position[]) => data),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('getPositions')) // Hide spinner on completion
      );
  }

  getContactsByRepoBusinessId(repoBusinessId?: number | null): Observable<RepoContact[]> {
    this.spinnerService.addRequest('getContactsByRepoBusinessId'); // Show spinner

    const url = repoBusinessId
      ? `${this.baseUrl}/api/RepoBusiness/${repoBusinessId}/Contacts`
      : `${this.baseUrl}/api/RepoBusiness/Contacts`;

    return this.http
      .get<RepoContact[]>(url)
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('getContactsByRepoBusinessId')) // Hide spinner
      );
  }

  getRepoContactsByMemberExcludingSale(saleId: number): Observable<RepoContact[]> {
    this.spinnerService.addRequest('getRepoContactsByMemberExcludingSale'); // Show spinner

    const url = `${this.baseUrl}/api/RepoBusiness/${saleId}/ContactsExcludingSale`;
   

    return this.http
      .get<RepoContact[]>(url)
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('getRepoContactsByMemberExcludingSale')) // Hide spinner
      );
  }

  getPositionById(positionId: number): Observable<Position> {
    this.spinnerService.addRequest('getPositionById'); // Show spinner
    const url = `${this.baseUrl}/api/RepoBusiness/${positionId}/Position`;
    return this.http
      .get<Position>(url)
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('getPositionById')) // Hide spinner
      );
  }


  getContactById(contactId: number): Observable<RepoContact> {
    this.spinnerService.addRequest('getContactById'); // Show spinner
    const url = `${this.baseUrl}/api/RepoBusiness/${contactId}/Contact`;
    return this.http
      .get<RepoContact>(url)
      .pipe(
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('getContactById')) // Hide spinner
      );
  }

  saveRepoBusiness(repoBusiness: RepoBusiness): Observable<RepoBusiness> {
    this.spinnerService.addRequest('saveRepoBusiness'); // Show spinner
    return this.http
      .post<RepoBusiness>(`${this.baseUrl}/api/RepoBusiness/RepoBusiness`, repoBusiness)
      .pipe(
        map((data: RepoBusiness) => data),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('saveRepoBusiness')) // Hide spinner on completion
      );
  }

  savePosition(position: Position): Observable<Position> {
    this.spinnerService.addRequest('savePosition'); // Show spinner
    return this.http
      .post<Position>(`${this.baseUrl}/api/RepoBusiness/Position`, position)
      .pipe(
        map((data: Position) => data),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('savePosition')) // Hide spinner on completion
      );
  }

  deletePosition(positionId: number): Observable<Position[]> {
    this.spinnerService.addRequest('deletePosition'); // Show spinner
    return this.http
      .get<Position[]>(`${this.baseUrl}/api/RepoBusiness/${positionId}/RemovePosition`)
      .pipe(
        map((data: Position[]) => data),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('deletePosition')) // Hide spinner on completion
      );
  }
  deleteBusiness(repoBusinessId: number): Observable<boolean>  {
    this.spinnerService.addRequest('deleteBusiness'); // Show spinner
    return this.http
      .delete<boolean>(`${this.baseUrl}/api/RepoBusiness/${repoBusinessId}/RemoveBusiness`)
      .pipe(
        map((data: boolean) => data),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('deleteBusiness')) // Hide spinner on completion
      );
  }
  saveRepoContact(repoContact: RepoContact): Observable<RepoContact> {
    this.spinnerService.addRequest('saveRepoContact'); // Show spinner
    return this.http
      .post<RepoContact>(`${this.baseUrl}/api/RepoBusiness/RepoContact`, repoContact)
      .pipe(
        map((data: RepoContact) => data),
        catchError((error: Error) => {
          this.onError.next(error.message);
          return throwError(error);
        }),
        finalize(() => this.spinnerService.popRequest('saveRepoContact')) // Hide spinner on completion
      );
  }

  public deleteContact(contactId: number): Observable<boolean> {
    this.spinnerService.addRequest('deleteContact');
    return this.http
      .delete<boolean>(`${this.baseUrl}/api/RepoBusiness/${contactId}/RemoveContact`, {
        withCredentials: false,
        observe: 'response',
      })
      .pipe(
        map((res) => res.body as boolean),
        catchError((error) => {
          console.error(error);
          return throwError(() => error);
        }),
        finalize(() => this.spinnerService.popRequest('deleteContact'))
      );
  }
}
